import React, { useState } from 'react';
import { useCustomerDocuments, useUploadCustomerDocument, useDeleteCustomerDocument, useKycDocumentTypes } from '../hooks';
import { Card, CardContent } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/FormField';
import { FormSelect } from '../../../components/ui/FormSelect';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/Dialog';
import { Skeleton } from '../../../components/ui/Skeleton';
import { useToast } from '../../../components/ui/Toast';
import { fetcherConfig } from '../../../lib/api-fetcher';

interface CustomerDocumentsTabProps {
  customerId: string;
}

interface KycDocumentType {
  id: string;
  code: string;
  label: string;
  validationPattern: string | null;
}

export function CustomerDocumentsTab({ customerId }: CustomerDocumentsTabProps) {
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [docType, setDocType] = useState('');
  const [docNumber, setDocNumber] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const { toast } = useToast();

  const { data: documentTypes, isLoading: docTypesLoading } = useKycDocumentTypes();
  const { data, isLoading, isError, refetch } = useCustomerDocuments(customerId);
  const uploadMutation = useUploadCustomerDocument(customerId);
  const deleteMutation = useDeleteCustomerDocument(customerId);

  const documents = data || [];

  const handleUploadSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) {
      toast({ type: 'error', title: 'Please select a file to upload' });
      return;
    }
    try {
      await uploadMutation.mutateAsync({ file, docType, docNumber: docNumber || undefined });
      toast({ type: 'success', title: 'Document uploaded successfully' });
      setIsUploadOpen(false);
      setDocType(documentTypes?.[0]?.code || '');
      setDocNumber('');
      setFile(null);
    // allow-silent-catch: error is surfaced to the user via toast(), not console/logger
    } catch (err: unknown) {
      toast({ type: 'error', title: 'Upload failed', description: err instanceof Error ? err.message : undefined });
    }
  };

  const handleDelete = async (documentId: string) => {
    if (!confirm('Are you sure you want to delete this document?')) return;
    try {
      await deleteMutation.mutateAsync(documentId);
      toast({ type: 'success', title: 'Document deleted' });
    // allow-silent-catch: error is surfaced to the user via toast(), not console/logger
    } catch (err: unknown) {
      toast({ type: 'error', title: 'Delete failed', description: err instanceof Error ? err.message : undefined });
    }
  };

  if (isLoading) return <Skeleton rows={4} />;
  if (isError) return (
    <Card className="border-danger-500">
      <CardContent className="p-6 text-center text-danger-600">
        Failed to load documents
        <Button variant="secondary" size="sm" className="ml-3" onClick={() => refetch()}>Retry</Button>
      </CardContent>
    </Card>
  );

  const docTypeOptions = (documentTypes || []).map((dt: KycDocumentType) => ({
    value: dt.code,
    label: dt.label,
  }));

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Documents</h3>
        <Button onClick={() => {
          setIsUploadOpen(true);
          if (!docType && documentTypes?.[0]) {
            setDocType(documentTypes[0].code);
          }
        }}>Upload Document</Button>
      </div>

      <Card className="border border-border shadow-sm overflow-hidden">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-surface-2">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-text-secondary uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {documents.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="px-4 py-8 text-center text-text-secondary">
                      No documents on file. Click "Upload Document" to add one.
                    </td>
                  </tr>
                ) : (
                  documents.map((doc) => (
                    <tr key={doc.id} className="hover:bg-surface-1">
                      <td className="px-4 py-3 text-sm">
                        <span className="px-2 py-0.5 rounded-full text-xs bg-accent-bg text-accent-text">
                          {doc.docType}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`px-2 py-0.5 rounded-full text-xs ${
                          doc.status === 'active' ? 'bg-success-bg text-success-text' : 'bg-surface-2 text-text-secondary'
                        }`}>
                          {doc.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <a
                            href={`${fetcherConfig.baseUrl}/common/storage/files/${doc.fileId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-accent-600 hover:underline text-sm"
                          >
                            Download
                          </a>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-danger-600"
                            onClick={() => handleDelete(doc.id)}
                            disabled={deleteMutation.isPending}
                          >
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={isUploadOpen} onOpenChange={setIsUploadOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUploadSubmit} className="space-y-4 p-4">
            {docTypesLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-8 w-full" />
              </div>
            ) : (
              <>
                <FormSelect
                  label="Document Type"
                  value={docType}
                  onChange={(e) => setDocType(e.target.value)}
                  options={docTypeOptions}
                />

                {(docType === 'PAN' || docType === 'AADHAAR') && (
                  <div>
                    <label className="block text-sm font-medium mb-1">{docType === 'PAN' ? 'PAN Number' : 'Aadhaar Number'}</label>
                    <Input value={docNumber} onChange={(e) => setDocNumber(e.target.value)} />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium mb-1">File</label>
                  <Input
                    type="file"
                    className="w-full"
                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  />
                </div>

                <DialogFooter className="mt-6">
                  <Button type="button" variant="secondary" onClick={() => setIsUploadOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" loading={uploadMutation.isPending}>
                    Upload Document
                  </Button>
                </DialogFooter>
              </>
            )}
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}